import React from 'react';
import { Box, Flex, Text } from '@qga/roo-ui/components';
import pluralize from 'pluralize';
import { equals } from 'ramda';

import { FlightSegment } from '@shared/v2/services/types/api/parsedResponse';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import { getFlightChargeData, savePoints } from '../../../utils';
import {
  ReturnFlightListOption,
  ReturnFlightListOptionProps,
} from './components/ReturnFlightListOption';

type ReturnFlightOption = ReturnFlightListOptionProps['flightOption'];

const getJoinedFlightNumbers = (flightSegments: FlightSegment[]): string =>
  flightSegments.map(({ flightNumber }) => flightNumber).join('-');

const getOptionKey = (flightOption: ReturnFlightOption): string => {
  const outboundKey = getJoinedFlightNumbers(
    flightOption.flightSegments.outbound,
  );
  const inboundKey = getJoinedFlightNumbers(
    flightOption.flightSegments.inbound,
  );
  return `outbound-${outboundKey}--inbound-${inboundKey}`;
};

export interface ReturnFlightListProps {
  selectedFlightOption: ReturnFlightOption;
  setSelectedFlightOption: (option: ReturnFlightOption) => void;
  flightOptions: ReturnFlightOption[];
  charges: Charges;
  offerId?: string;
}

export const ReturnFlightList = ({
  selectedFlightOption,
  setSelectedFlightOption,
  flightOptions,
  charges,
  offerId,
}: ReturnFlightListProps) => {
  const { points: allPoints, setPoints } = useCustomerSelection();
  const savePointsToSessionStorage = savePoints(setPoints);

  return (
    <Box>
      <Text fontWeight="bold">{`${pluralize(
        'Return flight',
        flightOptions.length,
        true,
      )} available`}</Text>
      <Flex flexDirection="column" pt={[3, null, null]}>
        {flightOptions?.map((flightOption) => {
          const isSelected = equals(selectedFlightOption, flightOption);

          const currentAmount = charges.total.amount;
          const additionalAmount = flightOption.charges.total.amount;
          const currentAdditionalAmount =
            selectedFlightOption.charges.total.amount;

          const { currentFraction, newFraction, newAmount } =
            getFlightChargeData({
              points: allPoints,
              offerId,
              currentTotalAmount: currentAmount,
              additionalAmount,
              currentAdditionalAmount,
            });

          return (
            <ReturnFlightListOption
              key={getOptionKey(flightOption)}
              flightOption={flightOption}
              isSelected={isSelected}
              charges={{
                selectedOption: selectedFlightOption.charges,
                option: flightOption.charges,
                offer: charges,
                current: {
                  amount: currentAmount,
                  fraction: currentFraction,
                },
              }}
              onSelect={() => {
                // We need to update session storage every time a flight is selected because the
                // PointsPlusPay component requires that info to render correct breakdown
                savePointsToSessionStorage({
                  offerId,
                  amount: newAmount,
                  fraction: newFraction,
                });

                setSelectedFlightOption(flightOption);
              }}
            />
          );
        })}
      </Flex>
    </Box>
  );
};
